import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Tune as TuneIcon,
  Gavel as ClausesIcon,
} from '@mui/icons-material';
import { Game, ToyType, GameCondition } from '../types';
import { inventoryService, toyTypeService } from '../services/api';
import UnitCountersDialog from '../components/UnitCountersDialog';
import ImageUpload from '../components/ImageUpload';
import ClausesManager from '../components/ClausesManager';

const Inventory: React.FC = () => {
  const [inventoryItems, setInventoryItems] = useState<Game[]>([]);
  const [toyTypes, setToyTypes] = useState<ToyType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterByToyType, setFilterByToyType] = useState<string>('');
  const [filterByHealthStatus, setFilterByHealthStatus] = useState<string>('');
  const [filterByAvailabilityStatus, setFilterByAvailabilityStatus] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openCountersDialog, setOpenCountersDialog] = useState(false);
  const [openClausesDialog, setOpenClausesDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Game | null>(null);
  const [editingItem, setEditingItem] = useState<Game | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    toy_type_id: '',
    quantity: 1,
    unit_price: 0,
    condition: GameCondition.EXCELLENT,
    image_base64: '',
    is_active: true,
    excellent_count: 0,
    good_count: 1,
    fair_count: 0,
    poor_count: 0,
    broken_count: 0,
    available_count: 1,
    in_use_count: 0,
    maintenance_count: 0,
    retired_count: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [itemsData, typesData] = await Promise.all([
        inventoryService.getAll(),
        toyTypeService.getAll(),
      ]);
      
      // Debug: verificar qué datos están llegando
      console.log('Items data received:', itemsData);
      console.log('First item with image:', itemsData.find(item => item.image_base64));
      
      setInventoryItems(itemsData);
      setToyTypes(typesData);
    } catch (err) {
      setError('Error al cargar los datos del inventario');
      console.error('Error fetching inventory data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item?: Game) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        toy_type_id: item.toy_type_id,
        quantity: item.quantity,
        unit_price: item.unit_price || 0,
        condition: item.condition || GameCondition.EXCELLENT,
        image_base64: item.image_base64 || '',
        is_active: item.is_active,
        // Mantener las cantidades actuales del producto
        excellent_count: item.excellent_count || 0,
        good_count: item.good_count || 0,
        fair_count: item.fair_count || 0,
        poor_count: item.poor_count || 0,
        broken_count: item.broken_count || 0,
        available_count: item.available_count || 0,
        in_use_count: item.in_use_count || 0,
        maintenance_count: item.maintenance_count || 0,
        retired_count: item.retired_count || 0,
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        toy_type_id: '',
        quantity: 1,
        unit_price: 0,
        condition: GameCondition.EXCELLENT,
        image_base64: '',
        is_active: true,
        // Valores por defecto para un producto nuevo
        excellent_count: 0,
        good_count: 1,
        fair_count: 0,
        poor_count: 0,
        broken_count: 0,
        available_count: 1,
        in_use_count: 0,
        maintenance_count: 0,
        retired_count: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({
      name: '',
      toy_type_id: '',
      quantity: 1,
      unit_price: 0,
      condition: GameCondition.EXCELLENT,
      image_base64: '',
      is_active: true,
      excellent_count: 0,
      good_count: 1,
      fair_count: 0,
      poor_count: 0,
      broken_count: 0,
      available_count: 1,
      in_use_count: 0,
      maintenance_count: 0,
      retired_count: 0,
    });
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        const updatedItem = await inventoryService.update(editingItem.id, formData);
        setInventoryItems(items => 
          items.map(item => item.id === updatedItem.id ? updatedItem : item)
        );
      } else {
        const newItem = await inventoryService.create(formData);
        setInventoryItems(items => [...items, newItem]);
      }
      handleCloseDialog();
    } catch (err) {
      setError('Error al guardar el producto');
      console.error('Error saving item:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este producto?')) {
      try {
        await inventoryService.delete(id);
        setInventoryItems(items => items.filter(item => item.id !== id));
      } catch (err) {
        setError('Error al eliminar el producto');
        console.error('Error deleting item:', err);
      }
    }
  };

  const handleOpenCountersDialog = (item: Game) => {
    setSelectedItem(item);
    setOpenCountersDialog(true);
  };

  const handleCloseCountersDialog = () => {
    setOpenCountersDialog(false);
    setSelectedItem(null);
  };

  const handleSaveCounters = (updatedItem: Game) => {
    setInventoryItems(items => 
      items.map(item => item.id === updatedItem.id ? updatedItem : item)
    );
  };

  const handleOpenClausesDialog = (item: Game) => {
    setSelectedItem(item);
    setOpenClausesDialog(true);
  };

  const handleCloseClausesDialog = () => {
    setOpenClausesDialog(false);
    setSelectedItem(null);
  };

  const handleClausesUpdated = (gameId: string) => {
    // Recargar el item específico para obtener las cláusulas actualizadas
    fetchData();
  };

  const getFilteredItems = () => {
    let filtered = inventoryItems;

    if (filterByToyType) {
      filtered = filtered.filter(item => item.toy_type_id === filterByToyType);
    }

    if (filterByHealthStatus) {
      filtered = filtered.filter(item => {
        switch (filterByHealthStatus) {
          case 'excellent':
            return (item.excellent_count || 0) > 0;
          case 'good':
            return (item.good_count || 0) > 0;
          case 'fair':
            return (item.fair_count || 0) > 0;
          case 'poor':
            return (item.poor_count || 0) > 0;
          case 'broken':
            return (item.broken_count || 0) > 0;
          default:
            return true;
        }
      });
    }

    if (filterByAvailabilityStatus) {
      filtered = filtered.filter(item => {
        switch (filterByAvailabilityStatus) {
          case 'available':
            return (item.available_count || 0) > 0;
          case 'in_use':
            return (item.in_use_count || 0) > 0;
          case 'maintenance':
            return (item.maintenance_count || 0) > 0;
          case 'retired':
            return (item.retired_count || 0) > 0;
          default:
            return true;
        }
      });
    }

    return filtered;
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'excellent': return '#4caf50';
      case 'good': return '#2196f3';
      case 'fair': return '#ff9800';
      case 'poor': return '#ff5722';
      case 'broken': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const getAvailabilityStatusColor = (status: string) => {
    switch (status) {
      case 'available': return '#4caf50';
      case 'in_use': return '#2196f3';
      case 'maintenance': return '#ff9800';
      case 'retired': return '#9e9e9e';
      default: return '#9e9e9e';
    }
  };


  const getTotalStats = () => {
    const totalItems = inventoryItems.length;
    const totalUnits = inventoryItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalAvailable = inventoryItems.reduce((sum, item) => sum + (item.available_count || 0), 0);
    const totalInUse = inventoryItems.reduce((sum, item) => sum + (item.in_use_count || 0), 0);
    const totalMaintenance = inventoryItems.reduce((sum, item) => sum + (item.maintenance_count || 0), 0);
    const totalRetired = inventoryItems.reduce((sum, item) => sum + (item.retired_count || 0), 0);

    return {
      totalItems,
      totalUnits,
      totalAvailable,
      totalInUse,
      totalMaintenance,
      totalRetired,
    };
  };

  const stats = getTotalStats();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Cargando inventario...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Inventario de Productos
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Agregar Producto
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Estadísticas generales */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="primary">
              {stats.totalItems}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Productos
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="primary">
              {stats.totalUnits}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Unidades
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="success.main">
              {stats.totalAvailable}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Disponibles
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="info.main">
              {stats.totalInUse}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              En Uso
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="warning.main">
              {stats.totalMaintenance}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mantenimiento
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              {stats.totalRetired}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Retirados
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de Juguete</InputLabel>
              <Select
                value={filterByToyType}
                onChange={(e) => setFilterByToyType(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {toyTypes.map(type => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado de Salud</InputLabel>
              <Select
                value={filterByHealthStatus}
                onChange={(e) => setFilterByHealthStatus(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="excellent">Excelente</MenuItem>
                <MenuItem value="good">Bueno</MenuItem>
                <MenuItem value="fair">Regular</MenuItem>
                <MenuItem value="poor">Malo</MenuItem>
                <MenuItem value="broken">Roto</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado de Disponibilidad</InputLabel>
              <Select
                value={filterByAvailabilityStatus}
                onChange={(e) => setFilterByAvailabilityStatus(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="available">Disponible</MenuItem>
                <MenuItem value="in_use">En Uso</MenuItem>
                <MenuItem value="maintenance">Mantenimiento</MenuItem>
                <MenuItem value="retired">Retirado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              onClick={() => {
                setFilterByToyType('');
                setFilterByHealthStatus('');
                setFilterByAvailabilityStatus('');
              }}
            >
              Limpiar Filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Lista de productos */}
      <Grid container spacing={3}>
        {getFilteredItems().map((item) => (
          <Grid item xs={12} md={6} lg={4} key={item.id}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
                transition: 'all 0.2s ease',
              }}
            >
              {/* Imagen del producto */}
              {item.image_base64 && item.image_base64.length > 0 && (
                <CardMedia
                  component="img"
                  height="200"
                  image={item.image_base64.startsWith('data:') ? item.image_base64 : `data:image/webp;base64,${item.image_base64}`}
                  alt={item.name}
                  sx={{ objectFit: 'cover' }}
                  onError={(e) => {
                    console.error('Error loading image for:', item.name, e);
                  }}
                  onLoad={() => {
                    console.log('Image loaded successfully for:', item.name);
                  }}
                />
              )}
              
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                  <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
                    {item.name}
                  </Typography>
                  <Box>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenCountersDialog(item)}
                      color="primary"
                      title="Gestionar Contadores"
                    >
                      <TuneIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenClausesDialog(item)}
                      color="secondary"
                      title="Gestionar Cláusulas"
                    >
                      <ClausesIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenDialog(item)}
                      color="primary"
                      title="Editar Juguete"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDelete(item.id)}
                      color="error"
                      title="Eliminar Juguete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {item.toy_type?.name || 'Sin tipo'}
                </Typography>

                <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                  Cantidad: {item.quantity}
                </Typography>

                {/* Contadores de estado de salud */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Estado de Salud:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {(item.excellent_count || 0) > 0 && (
                      <Chip 
                        label={`${item.excellent_count || 0} Excelente`} 
                        size="small" 
                        sx={{ backgroundColor: getHealthStatusColor('excellent'), color: 'white' }}
                      />
                    )}
                    {(item.good_count || 0) > 0 && (
                      <Chip 
                        label={`${item.good_count || 0} Bueno`} 
                        size="small" 
                        sx={{ backgroundColor: getHealthStatusColor('good'), color: 'white' }}
                      />
                    )}
                    {(item.fair_count || 0) > 0 && (
                      <Chip 
                        label={`${item.fair_count || 0} Regular`} 
                        size="small" 
                        sx={{ backgroundColor: getHealthStatusColor('fair'), color: 'white' }}
                      />
                    )}
                    {(item.poor_count || 0) > 0 && (
                      <Chip 
                        label={`${item.poor_count || 0} Malo`} 
                        size="small" 
                        sx={{ backgroundColor: getHealthStatusColor('poor'), color: 'white' }}
                      />
                    )}
                    {(item.broken_count || 0) > 0 && (
                      <Chip 
                        label={`${item.broken_count || 0} Roto`} 
                        size="small" 
                        sx={{ backgroundColor: getHealthStatusColor('broken'), color: 'white' }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Contadores de disponibilidad */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Disponibilidad:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {(item.available_count || 0) > 0 && (
                      <Chip 
                        label={`${item.available_count || 0} Disponible`} 
                        size="small" 
                        sx={{ backgroundColor: getAvailabilityStatusColor('available'), color: 'white' }}
                      />
                    )}
                    {(item.in_use_count || 0) > 0 && (
                      <Chip 
                        label={`${item.in_use_count || 0} En Uso`} 
                        size="small" 
                        sx={{ backgroundColor: getAvailabilityStatusColor('in_use'), color: 'white' }}
                      />
                    )}
                    {(item.maintenance_count || 0) > 0 && (
                      <Chip 
                        label={`${item.maintenance_count || 0} Mantenimiento`} 
                        size="small" 
                        sx={{ backgroundColor: getAvailabilityStatusColor('maintenance'), color: 'white' }}
                      />
                    )}
                    {(item.retired_count || 0) > 0 && (
                      <Chip 
                        label={`${item.retired_count || 0} Retirado`} 
                        size="small" 
                        sx={{ backgroundColor: getAvailabilityStatusColor('retired'), color: 'white' }}
                      />
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog para crear/editar producto */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem ? 'Editar Producto' : 'Agregar Nuevo Producto'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Imagen del producto - Movida al inicio */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Imagen del Producto
              </Typography>
              <ImageUpload
                value={formData.image_base64}
                onChange={(base64) => setFormData({ ...formData, image_base64: base64 })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nombre del Producto"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Tipo de Juguete</InputLabel>
                <Select
                  value={formData.toy_type_id}
                  onChange={(e) => setFormData({ ...formData, toy_type_id: e.target.value })}
                >
                  {toyTypes.map(type => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Cantidad Total"
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                fullWidth
                required
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Precio Unitario"
                type="number"
                value={formData.unit_price}
                onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })}
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Condición</InputLabel>
                <Select
                  value={formData.condition}
                  label="Condición"
                  onChange={(e) => setFormData({ ...formData, condition: e.target.value as GameCondition })}
                >
                  <MenuItem value={GameCondition.EXCELLENT}>Excelente</MenuItem>
                  <MenuItem value={GameCondition.GOOD}>Bueno</MenuItem>
                  <MenuItem value={GameCondition.FAIR}>Regular</MenuItem>
                  <MenuItem value={GameCondition.NEEDS_REPAIR}>Necesita Reparación</MenuItem>
                  <MenuItem value={GameCondition.OUT_OF_SERVICE}>Fuera de Servicio</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                💡 <strong>Tip:</strong> Para gestionar los estados de salud y disponibilidad de las unidades, 
                usa el botón de ajustes (⚙️) en la tarjeta del producto después de guardarlo.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            {editingItem ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para gestionar contadores de unidades */}
      <UnitCountersDialog
        open={openCountersDialog}
        onClose={handleCloseCountersDialog}
        item={selectedItem}
        onSave={handleSaveCounters}
      />

      {/* Dialog para gestionar cláusulas */}
      <Dialog
        open={openClausesDialog}
        onClose={handleCloseClausesDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Gestionar Cláusulas
        </DialogTitle>
        <DialogContent>
          {selectedItem && (
            <ClausesManager
              game={selectedItem}
              onClausesUpdated={handleClausesUpdated}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClausesDialog}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inventory;